import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { searchSuggestions, clearSuggestion } from 'data/search/actions'
import { selectSuggestion } from 'data/search/selectors'
import css from './style.scss'

const RADIUS_OPTIONS = [
  { value: 1, label: 'One mile' },
  { value: 2, label: '2 miles' },
  { value: 3, label: '3 miles' },
  { value: 5, label: '5 miles' },
  { value: 10, label: '10 miles' },
]

const CLASSIFICATION_OPTIONS = [
  { value: 'LANDLORD', label: 'Landlord' },
  { value: 'FLIPPER', label: 'Flipper' },
  { value: 'HOME BUILDER', label: 'Home Builder' },
  { value: 'IBUYER', label: 'iBuyer' },
  { value: 'REAL ESTATE INVESTMENT AND DEVELOPMENT', label: 'RE Investment & Dev' },
  { value: 'LAND BANK', label: 'Land Bank' },
  { value: 'COMMERCIAL LAND OWNER', label: 'Commercial Land Owner' },
  { value: 'TRUST', label: 'Trust' },
  { value: 'TITLE COMPANY', label: 'Title Company' },
  { value: 'FINANCING', label: 'Financing' },
  { value: 'UTILITY COMPANY', label: 'Utility Company' },
]

const PROPERTY_TYPE_OPTIONS = [
  { value: 'RESIDENTIAL', label: 'Residential' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'OFFICE', label: 'Office' },
  { value: 'RECREATIONAL', label: 'Recreational' },
  { value: 'INDUSTRIAL', label: 'Industrial' },
  { value: 'AGRICULTURAL', label: 'Agricultural' },
  { value: 'VACANT LAND', label: 'Vacant Land' },
  { value: 'EXEMPT', label: 'Exempt' },
  { value: 'MISCELLANEOUS', label: 'Miscellaneous' },
]

export default function SubjectPropertyInput({
  subjectProperty,
  radius,
  classifications,
  propertyTypes,
  loading,
  onSubjectChange,
  onRadiusChange,
  onClassificationsChange,
  onPropertyTypesChange,
  onSearch,
}) {
  const dispatch = useDispatch()
  const suggestion = useSelector(selectSuggestion)
  const items = suggestion.get('items')?.toJS?.() || []
  const suggestionLoading = suggestion.get('loading') || false

  const [inputValue, setInputValue] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [showBuyerDropdown, setShowBuyerDropdown] = useState(false)
  const [showPropertyTypeDropdown, setShowPropertyTypeDropdown] = useState(false)
  const [propertyTypeSearch, setPropertyTypeSearch] = useState('')
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const buyerDropdownRef = useRef(null)
  const propertyTypeDropdownRef = useRef(null)

  const label = subjectProperty?.get?.('label') || ''

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
      if (buyerDropdownRef.current && !buyerDropdownRef.current.contains(e.target)) {
        setShowBuyerDropdown(false)
      }
      if (propertyTypeDropdownRef.current && !propertyTypeDropdownRef.current.contains(e.target)) {
        setShowPropertyTypeDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleInputChange = (e) => {
    const val = e.target.value
    setInputValue(val)
    if (val.length >= 3) {
      dispatch(searchSuggestions(val))
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
      dispatch(clearSuggestion())
    }
  }

  const handleSelect = (item) => {
    setInputValue(item.label || '')
    setShowDropdown(false)
    dispatch(clearSuggestion())
    onSubjectChange(item)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && subjectProperty) {
      setShowDropdown(false)
      onSearch()
    }
  }

  return (
    <div className={css.subjectInput}>
      <div className={css.subjectLabel}>SUBJECT PROPERTY</div>

      <div className={css.addressInputWrap}>
        <input
          ref={inputRef}
          className={css.addressField}
          type="text"
          value={inputValue || label}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 3 && items.length > 0 && setShowDropdown(true)}
          placeholder="Enter property address..."
        />
        {showDropdown && (items.length > 0 || suggestionLoading) && (
          <div ref={dropdownRef} className={css.suggestDropdown}>
            {suggestionLoading && (
              <div className={css.suggestItem} style={{ color: '#66707D' }}>Loading...</div>
            )}
            {items.map((item, i) => (
              <div
                key={i}
                className={css.suggestItem}
                onClick={() => handleSelect(item)}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={css.controlsRow}>
        <div className={css.filterGroup}>
          <div className={css.filterLabel}>Search Radius</div>
          <select
            className={css.filterSelect}
            value={radius}
            onChange={(e) => onRadiusChange(Number(e.target.value))}
          >
            {RADIUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Property Type filter - hidden for now
        <div className={css.filterGroup} ref={propertyTypeDropdownRef}>
          <div className={css.filterLabel}>Property Type</div>
          <div
            className={css.multiSelectTrigger}
            onClick={() => setShowPropertyTypeDropdown(!showPropertyTypeDropdown)}
          >
            {propertyTypes.length === 0
              ? 'All Types'
              : propertyTypes.length === 1
                ? PROPERTY_TYPE_OPTIONS.find((o) => o.value === propertyTypes[0])?.label
                : `${propertyTypes.length} selected`}
            <span className={css.multiSelectArrow}>{showPropertyTypeDropdown ? '\u25B2' : '\u25BC'}</span>
          </div>
          {showPropertyTypeDropdown && (
            <div className={css.multiSelectDropdown}>
              <div className={css.multiSelectSearch}>
                <input
                  type="text"
                  placeholder="Search"
                  value={propertyTypeSearch}
                  onChange={(e) => setPropertyTypeSearch(e.target.value)}
                  className={css.multiSelectSearchInput}
                />
              </div>
              <label className={css.multiSelectItem}>
                <input
                  type="checkbox"
                  checked={propertyTypes.length === 0}
                  onChange={() => onPropertyTypesChange([])}
                />
                <span className={css.multiSelectLabel}>Select All</span>
              </label>
              <div className={css.multiSelectDivider} />
              {PROPERTY_TYPE_OPTIONS
                .filter((opt) => opt.label.toLowerCase().includes(propertyTypeSearch.toLowerCase()))
                .map((opt) => (
                  <label key={opt.value} className={css.multiSelectItem}>
                    <input
                      type="checkbox"
                      checked={propertyTypes.includes(opt.value)}
                      onChange={() => {
                        const next = propertyTypes.includes(opt.value)
                          ? propertyTypes.filter((v) => v !== opt.value)
                          : [...propertyTypes, opt.value]
                        onPropertyTypesChange(next)
                      }}
                    />
                    <span className={css.multiSelectLabel}>{opt.label}</span>
                  </label>
                ))}
            </div>
          )}
        </div>
        */}

        {/* Buyer Type filter - hidden for now
        <div className={css.filterGroup} ref={buyerDropdownRef}>
          <div className={css.filterLabel}>Buyer Type</div>
          <div
            className={css.multiSelectTrigger}
            onClick={() => setShowBuyerDropdown(!showBuyerDropdown)}
          >
            {classifications.length === 0 || classifications.length === CLASSIFICATION_OPTIONS.length
              ? 'All Buyers'
              : classifications.length === 1
                ? CLASSIFICATION_OPTIONS.find((o) => o.value === classifications[0])?.label
                : `${classifications.length} selected`}
            <span className={css.multiSelectArrow}>{showBuyerDropdown ? '\u25B2' : '\u25BC'}</span>
          </div>
          {showBuyerDropdown && (
            <div className={css.multiSelectDropdown}>
              <label className={css.multiSelectItem}>
                <input
                  type="checkbox"
                  checked={classifications.length === 0 || classifications.length === CLASSIFICATION_OPTIONS.length}
                  onChange={() => {
                    if (classifications.length === CLASSIFICATION_OPTIONS.length) {
                      onClassificationsChange([])
                    } else {
                      onClassificationsChange(CLASSIFICATION_OPTIONS.map((o) => o.value))
                    }
                  }}
                />
                <span className={css.multiSelectLabel}>Select All</span>
              </label>
              <div className={css.multiSelectDivider} />
              {CLASSIFICATION_OPTIONS.map((opt) => (
                <label key={opt.value} className={css.multiSelectItem}>
                  <input
                    type="checkbox"
                    checked={classifications.includes(opt.value)}
                    onChange={() => {
                      const next = classifications.includes(opt.value)
                        ? classifications.filter((v) => v !== opt.value)
                        : [...classifications, opt.value]
                      onClassificationsChange(next)
                    }}
                  />
                  <span className={css.multiSelectLabel}>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        */}
      </div>

      {subjectProperty && (
        <div className={css.selectedAddress}>
          <div className={css.selectedLabel}>{label}</div>
          <div className={css.selectedMeta}>
            {RADIUS_OPTIONS.find((o) => o.value === radius)?.label || `${radius} miles`} radius
          </div>
        </div>
      )}

      <button
        className={css.searchButton}
        onClick={onSearch}
        disabled={!subjectProperty || loading}
      >
        {loading ? <span className={css.spinner} /> : 'Find & Rank Cash Buyers'}
      </button>
    </div>
  )
}
